import {
  Component,
  OnDestroy,
  OnInit,
  viewChild,
  inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  ConfirmationService,
  FilterMetadata,
  PrimeTemplate,
} from '@openng/optimus-ui/api';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { TabService } from 'src/app/core/services/tab.service';
import { Contact } from 'src/app/model/contacts/contact';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from '../../../core/containers/search-container/search-container.component';
import {
  dynamicSort,
  isNullOrEmptyArray,
} from '../../../core/functions/common-functions';
import { EmailGroup } from '../../../model/admin/email-group';
import { EmailGroupInput } from '../../../model/admin/email-group-input';
import { EmailGroupMember } from '../../../model/admin/email-group-member';
import { DirtyStatus } from '../../../model/common/dirty-status';
import { AdminApiService } from '../../../services/api/admin-api.service';
import { AdminBuilderService } from '../../../services/builder/admin-builder.service';
import { EmailGroupTableService } from '../../../services/table/email-group-table.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { LogAndMessageService } from '../../../services/log-and-message.service';
import { EMAIL_GROUP_TABLE_DEFINITION } from '../../definitions/email-group-table-definition';
import { BusinessUnit } from 'src/app/model/admin/business-unit';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { ListboxChangeEvent, Listbox } from '@openng/optimus-ui/listbox';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
  AutoComplete,
} from '@openng/optimus-ui/autocomplete';
import { CheckboxChangeEvent, Checkbox } from '@openng/optimus-ui/checkbox';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BusinessUnitFilterComponent as BusinessUnitFilterComponent_1 } from '../../../core/components/business-unit-filter/business-unit-filter.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { InputText } from '@openng/optimus-ui/inputtext';
import { PickList } from '@openng/optimus-ui/picklist';

@Component({
  selector: 'app-email-group-container',
  templateUrl: './email-group-container.component.html',
  styleUrls: ['./email-group-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    BusinessUnitFilterComponent_1,
    ButtonDirective,
    GeneralGridComponent,
    Dialog,
    FormsModule,
    ReactiveFormsModule,
    InputText,
    Checkbox,
    PickList,
    PrimeTemplate,
    AutoComplete,
    Listbox,
    TranslatePipe,
  ],
})
export class EmailGroupContainerComponent
  extends SearchContainerComponent<EmailGroup>
  implements OnInit, OnDestroy
{
  protected _translateService = inject(TranslateService);
  protected _fb = inject(UntypedFormBuilder);
  protected _logger = inject(LogAndMessageService);
  protected _emailGroupTableService = inject(EmailGroupTableService);
  protected _adminApi = inject(AdminApiService);
  protected _authApi = inject(AuthApiService);
  protected _adminBuilder = inject(AdminBuilderService);
  protected _contactApi = inject(ContactApiService);
  protected _confirmationService = inject(ConfirmationService);
  protected _tabService = inject(TabService);
  protected _cdRef = inject(ChangeDetectorRef);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');

  canCreate = false;
  canEdit = false;
  canDelete = false;
  saving = false;
  emailGroupForm!: UntypedFormGroup;
  displayDialog = false;
  allContacts: any[] = [];
  filteredContacts: any[] = [];

  emailGroups: EmailGroup[] = [];
  members: EmailGroupMember[] = [];
  selectedGroup?: EmailGroup;
  selectedMember?: EmailGroupMember;
  contacts: any[] = [];
  sub!: Subscription;
  hasLoaded = false;

  availableBusinessUnits: BusinessUnit[] = [];
  sourcePicklist: BusinessUnit[] = [];
  targetPicklist: BusinessUnit[] = [];

  ngOnInit(): void {
    this.setTableService(this._emailGroupTableService);
    this.emailGroupForm = this._fb.group({
      id: [null],
      name: [{ value: null, disabled: true }, Validators.required],
      enabled: [{ value: false, disabled: true }],
      contact: [{ value: null, disabled: true }],
      emailAddress: [{ value: null, disabled: true }],
      version: [null],
      businessUnits: [{ value: null, disabled: false }],
    });
    this.initialize();

    this._contactApi
      .getContacts()
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.allContacts = response;
        },
      });
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  initialize(): void {
    this._translateService
      .get('ADMIN.SCREEN.EMAIL_GROUPS')
      .subscribe((label) => {
        this._tabService.updateActiveTabLabel(label);
        this.screenName = label;
      });

    this._adminApi.getAvailableBusinessUnits().subscribe((businessUnits) => {
      this.availableBusinessUnits = businessUnits;
    });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.EMAIL_GROUPS.CREATE_EMAIL_GROUPS,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.EMAIL_GROUPS.EDIT_EMAIL_GROUPS,
    ]);
    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.ADMINISTRATION.EMAIL_GROUPS.DELETE_EMAIL_GROUPS,
    ]);
    this.emailGroupForm.disable();
    this.columns = EMAIL_GROUP_TABLE_DEFINITION;
    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ): void {
    const query: { [key: string]: any } = {
      name: null,
      enabled: null,
      businessUnits: null,
      businessUnitString: null,
    };

    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    query.businessUnits = selectedBusinessUnits;
    if (filters.name) {
      query.name = (filters.name as FilterMetadata).value;
    }
    if (filters.enabled) {
      query.enabled = (filters.enabled as FilterMetadata).value.toString();
    }
    if (filters.businessUnitString) {
      query.businessUnitString = (
        filters.businessUnitString as FilterMetadata
      ).value;
    }

    this.queryNetwork(req, $event, query);
  }

  handleBusinessUnitChange(): void {
    this.lazyLoad({ lazy: this._emailGroupTableService.getLastLazyLoad()! });
  }

  showMemberList(): boolean {
    if (isNullOrEmptyArray(this.members)) {
      return false;
    } else {
      return true;
    }
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'name');
    this._adminApi
      .queryForEmailGroups(req.pageSize, req.page, query, sort)
      .subscribe({
        next: (emailGroups) => {
          this.emailGroups = emailGroups;
          this.totalRecords = emailGroups.length;
          this.elements = [...this.emailGroups];
          this.loading = false;
          this.hasLoaded = true;
          this._cdRef.detectChanges();
        },
        error: (error) => {
          this._logger.errorLogOnly(error);
          this._logger.translateToErrorAlert({
            bodyKey: 'ADMIN.MESSAGES.ERROR.QUERY_EMAIL_GROUPS',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
          this.loading = false;
        },
      });
  }

  newEmailGroup(): void {
    this.emailGroupForm.reset();
    this.emailGroupForm.controls['enabled'].patchValue(false, {
      emitEvent: true,
    });
    this.emailGroupForm.controls['contact'].disable();
    this.emailGroupForm.controls['emailAddress'].disable();
    this.members = [];
    this.sourcePicklist = [];
    this.targetPicklist = [];
    this.availableBusinessUnits.forEach((x) => {
      this.sourcePicklist.push(x);
    });

    if (this.canCreate) {
      this.emailGroupForm.enable();
    } else {
      this.emailGroupForm.disable();
    }
    this.displayDialog = true;
  }

  saveEmailGroup(): void {
    if (!this.canEdit && !this.canCreate) {
      return;
    }

    if (!isNullOrUndefined(this.emailGroupForm) && this.emailGroupForm.valid) {
      let emailGroup = null;
      if (!isNullOrUndefined(this.selectedGroup)) {
        emailGroup = this.selectedGroup;
      } else {
        emailGroup = Object.assign({}, this.emailGroupForm.getRawValue());
      }
      if (!emailGroup.id) {
        this.createEmailGroup(emailGroup);
      } else {
        this.updateEmailGroup(emailGroup, true);
      }
    }
  }

  createEmailGroup(emailGroup: EmailGroup): void {
    if (!this.canCreate) {
      return;
    }

    emailGroup.dirtyStatus = DirtyStatus.NEW;
    const emailGroupInput: EmailGroupInput | null =
      this._adminBuilder.buildEmailGroupInput(emailGroup, this.targetPicklist);

    this.saving = true;
    this._adminApi.createEmailGroup(emailGroupInput).subscribe({
      next: (result) => {
        this.clearForm();
        this.displayDialog = false;
        this.lazyLoad({
          lazy: this._emailGroupTableService.getLastLazyLoad()!,
        });
      },
      error: (error) => {
        this.saving = false;
        this._logger.errorLogOnly(error);
        this._logger.translateToErrorAlert({
          bodyKey: 'ADMIN.MESSAGES.ERROR.CREATE_EMAIL_GROUP',
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        this.saving = false;
      },
    });
  }

  updateEmailGroup(emailGroup: EmailGroup, closeDialog: boolean): void {
    if (!this.canEdit) {
      return;
    }
    emailGroup.dirtyStatus = DirtyStatus.UPDATED;
    const emailGroupInput: EmailGroupInput | null =
      this._adminBuilder.buildEmailGroupInput(emailGroup, this.targetPicklist);
    this.saving = true;
    this._adminApi.updateEmailGroup(emailGroup.id, emailGroupInput).subscribe({
      next: (result) => {
        if (!closeDialog && !isNullOrUndefined(result)) {
          this.changeViewEvent({ data: result });
        } else {
          this.clearForm();
          this.displayDialog = false;
        }
      },
      error: (error) => {
        this.saving = false;
        this._logger.errorLogOnly(error);
        this._logger.translateToErrorAlert({
          bodyKey: 'ADMIN.MESSAGES.ERROR.UPDATE_EMAIL_GROUP',
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        // Reset these if they had something in them
        this.emailGroupForm.controls['contact'].patchValue(null, {
          emitEvent: true,
        });
        this.emailGroupForm.controls['emailAddress'].patchValue(null, {
          emitEvent: true,
        });
        this.saving = false;
      },
    });
  }

  deleteEmailGroup(): void {
    if (!this.canDelete) {
      return;
    }

    if (!isNullOrUndefined(this.emailGroupForm)) {
      const emailGroup = Object.assign({}, this.emailGroupForm.getRawValue());
      let confHeader = null,
        ok = null,
        cancel = null,
        message = null;
      const confHeader$ = this._translateService.get(
        'ADMIN.MESSAGES.HEADERS.DELETE_GROUP'
      );
      const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
      const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
      const message$ = this._translateService.get(
        'ADMIN.MESSAGES.CONFIRMATION.DELETE_GROUP',
        { name: emailGroup.name }
      );

      forkJoin([confHeader$, ok$, cancel$, message$]).subscribe((messages) => {
        confHeader = messages[0];
        ok = messages[1];
        cancel = messages[2];
        message = messages[3];
      });
      this._confirmationService.confirm({
        header: confHeader!,
        icon: 'fa fa-question-circle',
        acceptVisible: true,
        rejectVisible: true,
        acceptLabel: ok!,
        rejectLabel: cancel!,
        message: message!,
        accept: () => {
          this.saving = true;
          if (!isNullOrUndefined(emailGroup.id)) {
            // Existing group so delete it
            this._adminApi.deleteEmailGroup(emailGroup.id).subscribe({
              next: (result) => {
                this.saving = false;
                this.clearForm();
                this.displayDialog = false;
              },
              error: (error) => {
                this._logger.errorLogOnly(error);
                this._logger.translateToErrorAlert({
                  bodyKey: 'ADMIN.MESSAGES.ERROR.DELETE_EMAIL_GROUP',
                  headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                });
              },
            });
          } else {
            // Group is new, so just close the dialog
            this.displayDialog = false;
          }
        },
        reject: () => {},
      });
    }
  }

  clearForm(): void {
    this.emailGroupForm.reset();
  }

  dialogClosed(): void {
    this.clearForm();
    this.selectedGroup = undefined;
    this.selectedMember = undefined;
    this.lazyLoad({ lazy: this._emailGroupTableService.getLastLazyLoad()! });
  }

  changeViewEvent($event: { data: EmailGroup }): void {
    this.selectedGroup = $event.data;
    this.emailGroupForm.patchValue({
      id: $event.data.id,
      name: $event.data.name,
      enabled: $event.data.enabled,
      version: $event.data.version,
      businessUnits: $event.data.businessUnits,
    });
    if (!isNullOrUndefined($event.data.id) && this.canEdit === true) {
      this.emailGroupForm.controls['emailAddress'].enable();
      this.emailGroupForm.controls['contact'].enable();
    }
    const businessUnits = this.emailGroupForm.controls['businessUnits'].value;
    if (businessUnits != null) {
      let currentBusinessUnits: string[] = [];
      businessUnits.forEach((x: BusinessUnit) => {
        currentBusinessUnits.push(x.name);
      });
      this.sourcePicklist = [];
      this.targetPicklist = [];
      this.availableBusinessUnits.forEach((x) => {
        if (currentBusinessUnits.includes(x.name)) {
          this.targetPicklist.push(x);
        } else {
          this.sourcePicklist.push(x);
        }
      });
    }
    this.members = [...$event.data.EmailGroupMembers];

    if (this.canEdit) {
      this.emailGroupForm.enable();
    } else {
      this.emailGroupForm.disable();
    }
    this.displayDialog = true;
  }

  memberSelected($event: ListboxChangeEvent): void {
    if (!isNullOrUndefined($event) && !isNullOrUndefined($event.value)) {
      this.selectedMember = $event.value;
    }
  }

  memberSearch($event: AutoCompleteCompleteEvent): void {
    const query = $event && $event.query ? $event.query.toLowerCase() : '';
    this.contacts = this.allContacts
      .sort(dynamicSort('firstName', 1))
      .filter((c) =>
        (c.firstName + ' ' + c.lastName).toLowerCase().includes(query)
      )
      .map((c: Contact) => this._adminBuilder.buildEmailGroupContact(c));
  }

  contactSelected($event: AutoCompleteSelectEvent): void {
    const eventValue = $event.value;
    if (!isNullOrUndefined(eventValue)) {
      this.emailGroupForm.controls['emailAddress'].patchValue(
        eventValue.address
      );
    }
  }

  addEmailGroupMember(): void {
    const emailGroup = this.emailGroupForm.getRawValue();
    if (
      !isNullOrUndefined(emailGroup) &&
      !isNullOrUndefined(emailGroup.emailAddress)
    ) {
      const nm: EmailGroupMember = this._adminBuilder.buildNewEmailGroupMember(
        emailGroup.emailAddress
      );
      this.selectedGroup = Object.assign({}, emailGroup);
      this.selectedGroup!.EmailGroupMembers = this.members;
      if (!isNullOrUndefined(this.selectedGroup)) {
        this.selectedGroup!.EmailGroupMembers = [
          ...this.selectedGroup!.EmailGroupMembers,
          nm,
        ];
        this.updateEmailGroup(this.selectedGroup!, false);
      }
    } else {
      this._logger.translateToErrorAlert({
        headerKey: 'ADMIN.MESSAGES.HEADERS.ADD_MEMBER',
        bodyKey: 'ADMIN.MESSAGES.ERROR.MISSING_EMAIL_ADDRESS',
      });
    }
  }

  deleteEmailGroupMember(): void {
    const emailGroup = this.emailGroupForm.getRawValue();
    if (
      !isNullOrUndefined(emailGroup) &&
      !isNullOrUndefined(this.selectedMember)
    ) {
      this.selectedMember!.dirtyStatus = DirtyStatus.DELETED;
      for (let i = 0; i < this.selectedGroup!.EmailGroupMembers.length; i++) {
        if (
          this.selectedGroup!.EmailGroupMembers[i].id == this.selectedMember!.id
        ) {
          this.selectedGroup!.EmailGroupMembers.splice(i, 1);
        }
      }
      if (!isNullOrUndefined(this.selectedGroup)) {
        this.updateEmailGroup(this.selectedGroup!, false);
      }
    } else {
      this._logger.translateToErrorAlert({
        headerKey: 'ADMIN.MESSAGES.HEADERS.REMOVE_MEMBER',
        bodyKey: 'ADMIN.MESSAGES.ERROR.NO_EMAIL_SELECTED',
      });
    }
  }

  toggleGroup($event: CheckboxChangeEvent): void {
    if (!isNullOrUndefined($event) && !isNullOrUndefined(this.selectedGroup)) {
      this.selectedGroup!.enabled = $event.checked;
    }
  }

  nameChanged(): void {
    if (!isNullOrUndefined(this.selectedGroup)) {
      this.selectedGroup!.name = this.emailGroupForm.controls['name'].value;
    }
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._emailGroupTableService.getLastLazyLoad()! });
  }
}
